<!-- Institute of Design and Innovation (email idi@unm.edu). -->

<!-- Sponsor page -->
<template>
  <div class="sponsors page">
    <div class="page-heading">
      <h1>Sponsors</h1>
      <div class="sponsor-search">
        <input type="text" v-model="filterKey" placeholder="Search">
        <i class="fal fa-search"></i>
      </div>
      <av-button @click="showCreateForm"><i class="fal fa-plus-circle"></i> Create</av-button>
    </div>

    <div class="page-content">
      <!-- List of Sponsors -->
      <div v-for="sponsor of filteredSponsors" :key="sponsor.id" class="page-listing sponsor-listing">
        <div class="name">{{ sponsor.name }}</div>
        <div class="associated-type">{{ sponsor.appointmentType ? sponsor.appointmentType.name : 'Not Assigned'}}</div>
        <div class="at-top"><span v-if="sponsor.toTop"><i  class="fal fa-thumbtack"></i> Pinned</span></div>
        <div class="actions">
          <a class="shaded black" @click="showUpdateForm(sponsor)"><i class="fal fa-edit"></i> Edit</a>
          <a class="shaded red" @click="removeSponsor(sponsor.id)"><i class="fal fa-trash"></i> Remove</a>
        </div>
      </div>

      <!-- Form -->
      <av-modal :visible="shouldShowSponsorForm" @close="shouldShowSponsorForm = false">
        <div class="modal-header">
          {{ isEditing ? 'Editing Sponsor' : 'New Sponsor' }}
        </div>
        <div class="modal-content">
          <div class="av-form-field">
            <label class="av-label">Name</label>
            <input type="text" class="av-input" v-model="sponsorForm.name" />
          </div>

          <div class="av-form-field">
            <label class="av-label">Associated Appointment Type</label>
            <av-select
              :items="appointmentTypes"
              displayKey="name"
              valueKey="id"
              placeholder="Select Appointment Type"
              v-model="sponsorForm.appointmentTypeId"
            ></av-select>
          </div>

          <div class="av-form-field">
            <av-checkbox v-model="sponsorForm.toTop">Pin to top of list.</av-checkbox>
          </div>
        </div>

        <div class="modal-actions">
          <av-button class="hollow error" @click="shouldShowSponsorForm = false">Cancel</av-button>
          <av-button v-if="isEditing" @click="updateSponsor" :disabled="!isValid || !isActive">Save</av-button>
          <av-button v-if="!isEditing" @click="createSponsor" :disabled="!isValid || !isActive">Create</av-button>
        </div>
      </av-modal>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import AppointmentTypeService from '../common/appointmentTypeService'
import SponsorService from '../common/sponsorService'
import { AppointmentType } from '../models/appointmentType'
import { Sponsor, SponsorDetail } from '../models/sponsor'

export default defineComponent({
  name: 'Sponsors',

  data () {
    return {
      isActive: true,

      /** list of appointment types */
      appointmentTypes: [] as Array<AppointmentType>,

      /** indicates if should show sponsor form */
      shouldShowSponsorForm: false,

      /** list of sponsors */
      sponsors: [] as Array<SponsorDetail>,

      /** sponsor form */
      sponsorForm: null as unknown as Sponsor,

      /** filter key */
      filterKey: ''
    }
  },

  computed: {
    /** Is Editing sponsor form */
    isEditing: function (): boolean {
      return this.sponsorForm && (this.sponsorForm.id !== 0)
    },

    /** Is Valid sponsor form */
    isValid: function (): boolean {
      return this.sponsorForm.name !== null && this.sponsorForm.appointmentTypeId !== null
    },

    /** Filtered Sponsors list */
    filteredSponsors (): Array<SponsorDetail> {
      if (this.filterKey !== '') {
        return this.sponsors.filter(s => RegExp(this.filterKey, 'i').test(s.name))
      } else {
        return this.sponsors
      }
    }
  },

  mounted () {
    // load sponsors and appoitnment types
    this.getSponsors()
    this.getAppointmentTypes()
  },

  methods: {
    /**
     * Get Appointment Types
     */
    getAppointmentTypes () {
      AppointmentTypeService.getPublic().then(res => {
        this.appointmentTypes = res.data
      })
    },

    /**
     * Show Create Form
     */
    showCreateForm () {
      this.sponsorForm = {
        id: 0,
        name: '',
        appointmentTypeId: null as unknown as number,
        toTop: false
      } as Sponsor

      this.shouldShowSponsorForm = true
    },

    /**
     * Show Update Form
     */
    showUpdateForm (sponsor: SponsorDetail) {
      if (sponsor.appointmentType != null) {
        this.sponsorForm = {
          id: sponsor.id,
          name: sponsor.name,
          appointmentTypeId: sponsor.appointmentType.id,
          toTop: sponsor.toTop
        }
      } else {
        this.sponsorForm = {
          id: sponsor.id,
          name: sponsor.name,
          appointmentTypeId: null,
          toTop: sponsor.toTop
        }
      }

      this.shouldShowSponsorForm = true
    },

    /**
     * Get Sponsors
     */
    getSponsors () {
      SponsorService.getAllDetailed().then(res => {
        this.sponsors = res.data.sort((a, b) => {
          if (a.toTop > b.toTop) {
            return -1
          } else if (a.toTop < b.toTop) {
            return 1
          } else {
            return 0
          }
        })
      })
    },

    /**
     * Create Sponsor
     */
    createSponsor () {
      this.isActive = false
      SponsorService.create(this.sponsorForm).then(res => {
        this.sponsors.push(res.data)
        this.shouldShowSponsorForm = false
        this.isActive = true
      })
    },

    /**
     * Update Sponsor
     */
    updateSponsor () {
      this.isActive = false
      SponsorService.update(this.sponsorForm.id, this.sponsorForm).then(res => {
        const sponsor = this.sponsors.find(s => s.id === this.sponsorForm.id)
        if (sponsor) {
          sponsor.name = res.data.name
          sponsor.appointmentType = res.data.appointmentType
        }
        this.shouldShowSponsorForm = false
        this.isActive = true
      })
    },

    /**
     * Remove Sponsor
     */
    removeSponsor (id: number) {
      if (window.confirm('Are you sure you want to Remove?')) {
        SponsorService.remove(id).then(res => {
          const ind = this.sponsors.findIndex(s => s.id === id)
          if (ind !== -1) {
            this.sponsors.splice(ind, 1)
          }
        })
      }
    }
  }
})
</script>
